import { Button, IconButton, IconButtonProps, Menu, MenuItem } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import React, { ReactNode, useState } from 'react';
import { StrippedButton, StrippedIconButton } from './StrippedButtons';

interface ActionOptions {
  disabled?: boolean;
  divider?: boolean;
}

export interface MenuActionsProps {
  noAutoClose?: boolean;
  actions: ([string,ReactNode, () => void] | [string,ReactNode, () => void, ActionOptions] | null)[];
  keepMenu?: boolean;
  className?: string;
  stopPropagation?: boolean;
}

interface Props extends MenuActionsProps, Pick<IconButtonProps, "size" | "color"> {
  icon?: ReactNode;
  strippedButton?: boolean;
}

/**
 * An icon button that opens up a menu with actions.
 * Each action comes in the form of: string key, label, click handler
 */
export const MoreActionsMenuButton = (props: Props) => {
  const [menuAnchor,setMenuAnchor] = useState<any>(null);

  const { actions, icon, noAutoClose, strippedButton, keepMenu, ...otherProps } = props;

  const availableActions = actions.filter(x => !!x) as [string,ReactNode, () => void, ActionOptions | undefined][];
  
  if(!availableActions.length) {
    return null;
  }
  
  return (<>
    {strippedButton
      ? (
          <StrippedIconButton
            className={props.className}
            onClick={e => { if(props.stopPropagation) { e.stopPropagation(); } setMenuAnchor(e.target)}}>
              {icon || <MoreVert />}
          </StrippedIconButton>)
      : (
          <IconButton
            size="small"
            className={props.className}
            {...otherProps}
            onClick={e =>{ if(props.stopPropagation) { e.stopPropagation(); } setMenuAnchor(e.target)}}>
              {icon || <MoreVert />}
          </IconButton>)}
    {(!!menuAnchor || keepMenu) &&
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)} keepMounted={keepMenu}>
        {availableActions
          .map(([key,label,action,options]) => key?.startsWith("divider")
            ? <MenuItem key={key} divider />
            : (
            <MenuItem
              divider={options?.divider}
              key={key}
              disabled={options?.disabled}
              onClick={e => {
                if(props.stopPropagation) {
                  e.stopPropagation();
                }
                action();
                if(!noAutoClose) {
                  setMenuAnchor(null);
                }
              }}>
                {label}
            </MenuItem>
          ))}
      </Menu>}
  </>)
}

interface PlainProps extends MenuActionsProps, Pick<IconButtonProps, "size" | "color"> {
  icon?: ReactNode;
  label: ReactNode;
  strippedButton?: boolean;
}

/**
 * An plain button that opens up a menu with actions.
 * Each action comes in the form of: string key, label, click handler
 */
export const MoreActionsPlainButton = (props: PlainProps) => {
  const [menuAnchor,setMenuAnchor] = useState<any>(null);

  const { actions, icon, noAutoClose, strippedButton, keepMenu, ...otherProps } = props;

  const availableActions = actions.filter(x => !!x) as [string,ReactNode, () => void, ActionOptions | undefined][];

  if(!availableActions.length) {
    return null;
  }
  
  return (<>
    {strippedButton
      ? <StrippedButton className={props.className} onClick={e => setMenuAnchor(e.target)}>{props.label}</StrippedButton>
      : <Button
        size="small"
        startIcon={props.icon}
        className={props.className}
        {...otherProps as any}
        onClick={e => setMenuAnchor(e.target)}>
          {props.label}
        </Button>}
    {(!!menuAnchor || keepMenu) &&
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)} keepMounted={keepMenu}>
        {availableActions
          .map(([key,label,action,options]) => key?.startsWith("divider")
            ? <MenuItem key={key} divider />
            : (
            <MenuItem divider={options?.divider} key={key} disabled={options?.disabled} onClick={() => { action(); if(!noAutoClose) { setMenuAnchor(null); }}}>{label}</MenuItem>
          ))}
      </Menu>}
  </>)
}
